import React, { useState, useMemo } from 'react';
import { useCRM } from '../../context/CRMContext';
import { useCRMData } from '../../context/CRMDataContext';
import SafeIcon from '../../common/SafeIcon';

const CompanyForm = ({ company, onClose }) => {
  const { users } = useCRM();
  const {
    addCompany,
    updateCompany,
    companies,
    ready,
    loading,
    activeTenantId
  } = useCRMData();
  const [formData, setFormData] = useState(company || {
    name: '', org_number: '', visiting_address: '', billing_address: '', city: '', country: 'Norge', website: '', logo: '', industry: '', size_segment: '10-49', owner_user_id: '', notes: '', gdpr_status: 'Usikker', status: 'Lead', tags: [], parent_company_id: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [tagColor, setTagColor] = useState('blue');

  const availableTags = useMemo(() => {
    const allTags = companies.flatMap(c => c.tags || []);
    const uniqueTags = Array.from(new Set(allTags.map(t => t.text))).map(text => allTags.find(t => t.text === text));
    return uniqueTags;
  }, [companies]);

  const availableParents = useMemo(() => {
    return companies.filter(c => c.company_id !== (company?.company_id || 'new'));
  }, [companies, company]);

  const colors = [
    { name: 'Blå', value: 'blue', bg: 'bg-blue-100', text: 'text-blue-800' },
    { name: 'Grønn', value: 'green', bg: 'bg-green-100', text: 'text-green-800' },
    { name: 'Rød', value: 'red', bg: 'bg-red-100', text: 'text-red-800' },
    { name: 'Gul', value: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-800' },
    { name: 'Lilla', value: 'purple', bg: 'bg-purple-100', text: 'text-purple-800' },
    { name: 'Grå', value: 'gray', bg: 'bg-gray-100', text: 'text-gray-800' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!ready || !activeTenantId) {
      console.error('[CompanyForm] Kan ikke lagre før aktiv organisasjon er klar.', {
        ready,
        loading,
        activeTenantId
      });
      return;
    }

    try {
      const result = company
        ? await updateCompany(company.company_id || company.id, formData)
        : await addCompany(formData);

      if (!result || result.success === false || !result.data) {
        console.error('[CompanyForm] Selskapet ble ikke lagret.', result?.error || result);
        return;
      }

      if (!import.meta.env.PROD) {
        console.debug('[CompanyForm] Selskap lagret.', {
          mode: company ? 'update' : 'add',
          result
        });
      }

      onClose();
    } catch (submitError) {
      console.error('[CompanyForm] Feil ved lagring av selskap.', submitError);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const newData = { ...prev, [name]: value };
      if (name === 'website') {
        newData.logo = value ? `https://www.google.com/s2/favicons?domain=${value}&sz=128` : '';
      }
      return newData;
    });
  };

  const addTag = (tagText = tagInput) => {
    if (tagText.trim()) {
      if (formData.tags?.some(t => t.text.toLowerCase() === tagText.toLowerCase())) {
        setTagInput('');
        return;
      }
      setFormData({ ...formData, tags: [...(formData.tags || []), { text: tagText.trim(), color: tagColor }] });
      setTagInput('');
    }
  };

  const removeTag = (index) => {
    const newTags = [...(formData.tags || [])];
    newTags.splice(index, 1);
    setFormData({ ...formData, tags: newTags });
  };

  const tenantUnavailable = !loading && (!ready || !activeTenantId);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-20 h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center shrink-0 overflow-hidden">
          {formData.logo ? (
            <img src={formData.logo} alt="Logo" className="w-full h-full object-contain" onError={(e) => e.target.style.display = 'none'} />
          ) : (
            <SafeIcon icon="🖼️" className="w-8 h-8 text-gray-400" />
          )}
        </div>
        <div className="flex-1">
          <p className="text-sm text-gray-500 italic mb-2"> Logo hentes automatisk når du legger inn nettside. </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Firmanavn</label>
          <input required name="name" value={formData.name} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Organisasjonsnummer</label>
          <input name="org_number" value={formData.org_number} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Bransje</label>
          <input name="industry" value={formData.industry} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Nettside</label>
          <div className="relative mt-1">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <SafeIcon icon="🌐" className="h-4 w-4 text-gray-400" />
            </div>
            <input name="website" value={formData.website} onChange={handleChange} placeholder="f.eks. firma.no" className="block w-full pl-10 border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
        </div>
      </div>
      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-3">
        <h4 className="text-sm font-semibold text-gray-900 flex items-center">
          <SafeIcon icon="📍" className="mr-2" /> Adresser
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Besøksadresse</label>
            <input name="visiting_address" value={formData.visiting_address} onChange={handleChange} placeholder="Gateadresse..." className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Fakturaadresse</label>
            <input name="billing_address" value={formData.billing_address} onChange={handleChange} placeholder="Postboks eller adresse..." className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Sted</label>
            <input name="city" value={formData.city} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Land</label>
            <input name="country" value={formData.country} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500" />
          </div>
        </div>
      </div>
      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
        <h4 className="text-sm font-semibold text-blue-900 flex items-center mb-2">
          <SafeIcon icon="ᛦ" className="mr-2" /> Selskapsstruktur
        </h4>
        <div>
          <label className="block text-sm font-medium text-blue-800">Morselskap</label>
          <select name="parent_company_id" value={formData.parent_company_id || ''} onChange={handleChange} className="mt-1 block w-full border border-blue-200 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500 bg-white">
            <option value="">-- Ingen (Toppnivå) --</option>
            {availableParents.map(c => (
              <option key={c.company_id} value={c.company_id}>{c.name}</option>
            ))}
          </select>
          <p className="text-xs text-blue-600 mt-1">Velg selskapet dette er en datter/avdeling av.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Eier (Selger)</label>
          <select name="owner_user_id" value={formData.owner_user_id} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="">Velg eier</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Størrelse (ansatte)</label>
          <select name="size_segment" value={formData.size_segment} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="1-9">1-9</option>
            <option value="10-49">10-49</option>
            <option value="50-249">50-249</option>
            <option value="250+">250+</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 p-4 rounded-md border border-gray-200">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">Status</label>
          <select name="status" value={formData.status || 'Lead'} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="Lead">Lead</option>
            <option value="Prospect">Prospect</option>
            <option value="Customer">Kunde</option>
            <option value="Partner">Partner</option>
            <option value="Inactive">Inaktiv</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-1">GDPR-status</label>
          <select name="gdpr_status" value={formData.gdpr_status} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500">
            <option value="Samtykke">Samtykke</option>
            <option value="Berettiget interesse">Berettiget interesse</option>
            <option value="Usikker">Usikker</option>
            <option value="Blokker kontakt">Blokker kontakt</option>
          </select>
        </div>
      </div>
      <div className="bg-gray-50 p-3 rounded-lg border border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Tags / Etiketter</label>
        {availableTags.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1">
            <span className="text-xs text-gray-500 mr-1 self-center">Forslag:</span>
            {availableTags.map((t, i) => (
              <button key={i} type="button" onClick={() => { setTagInput(t.text); setTagColor(t.color); addTag(t.text); }} className="px-2 py-0.5 text-xs bg-white border border-gray-300 rounded-full hover:bg-blue-50 hover:border-blue-300 transition-colors">
                {t.text}
              </button>
            ))}
          </div>
        )}
        <div className="flex gap-2 mb-2">
          <input type="text" placeholder="Ny tag (f.eks. VIP)" value={tagInput} onChange={(e) => setTagInput(e.target.value)} className="flex-1 border border-gray-300 rounded-md p-2 text-sm" list="tag-suggestions" />
          <datalist id="tag-suggestions">
            {availableTags.map((t, i) => (
              <option key={i} value={t.text} />
            ))}
          </datalist>
          <select value={tagColor} onChange={(e) => setTagColor(e.target.value)} className="border border-gray-300 rounded-md p-2 text-sm w-32">
            {colors.map(c => <option key={c.value} value={c.value}>{c.name}</option>)}
          </select>
          <button type="button" onClick={() => addTag()} className="px-3 py-2 bg-gray-200 rounded-md hover:bg-gray-300 text-gray-700">
            <SafeIcon icon="➕" className="w-4 h-4" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {formData.tags && formData.tags.map((tag, idx) => {
            const colorObj = colors.find(c => c.value === tag.color) || colors[0];
            return (
              <span key={idx} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorObj.bg} ${colorObj.text} border border-opacity-20`}>
                {tag.text}
                <button type="button" onClick={() => removeTag(idx)} className="ml-1 hover:text-red-600">
                  <SafeIcon icon="✕" className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700">Notater</label>
        <textarea name="notes" rows="3" value={formData.notes} onChange={handleChange} className="mt-1 block w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"></textarea>
      </div>
      <div className="flex justify-end pt-4">
        {tenantUnavailable && (
          <p className="mr-4 self-center text-sm text-amber-700">
            Kan ikke lagre før aktiv organisasjon er klar.
          </p>
        )}
        <button
          type="submit"
          disabled={loading || !ready || !activeTenantId}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <SafeIcon icon="💾" className="w-4 h-4 mr-2" /> Lagre Selskap
        </button>
      </div>
    </form>
  );
};

export default CompanyForm;